import React, { useCallback, useMemo } from 'react'
import { matchPath, match as RouterMatch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { partyApiSelectors } from '../../../api/party'
import { subscribe } from 'react-contextual'
import { path, pathOr } from 'ramda'
import { ROUTES } from 'constants/routes'
import { ContactsProvider } from '../../../contacts'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { $TSFixMe } from 'types/ts-migrate'
import { RootState } from 'src/state/types'

type StatementFormat = 'json' | 'pdf' | 'csv'

const STATEMENT_EVENTS = {
  json: accountingApiEvents.customerStatementJson_request,
  pdf: accountingApiEvents.customerStatementPdf_request,
  csv: accountingApiEvents.customerStatementCsv_request,
}

// Tenant wallet substitutes: same key, per-lease Tenant Statement endpoint.
const TENANT_STATEMENT_EVENTS = {
  pdf: accountingApiEvents.tenantStatementPdf_request,
  csv: accountingApiEvents.tenantStatementCsv_request,
}

interface CustomerStatementProps {
  match: RouterMatch
  redirect: (path: string) => void
  contactFilterSearch: string
}

const CustomerStatement = ({
  match,
  redirect,
  contactFilterSearch,
}: CustomerStatementProps): null | React.ReactElement => {
  const editMatchPath = matchPath(match.url, {
    path: ROUTES.contactStatement,
    exact: true,
    strict: false,
  })

  const customerId: $TSFixMe = path(['params', 'id'], editMatchPath)
  const accountId: $TSFixMe = path(['params', 'accountId'], editMatchPath)
  const dispatch = useDispatch()

  // On a lease's Tenant wallet the PDF/CSV downloads are the per-lease Tenant
  // Statement (CU-869dmfep1, Anri) — the on-screen Account Statement view is
  // unchanged, only the downloads swap. Any other wallet (Deposit,
  // Application, no attached lease) keeps the Account Statement downloads.
  const account = useSelector((state: RootState) =>
    partyApiSelectors.getPartyAccountByAccountId(state)(customerId, accountId),
  )
  const tenantLeasePortfolioId = account?.tag === 'Tenant' ? account.portfolioId ?? null : null
  const isTenantLeaseWallet = Boolean(tenantLeasePortfolioId)

  const fetchCustomerStatement = useCallback(
    (startDate: string, endDate: string, format: StatementFormat = 'json') => {
      if (customerId && accountId) {
        if (format !== 'json' && tenantLeasePortfolioId) {
          dispatch(
            TENANT_STATEMENT_EVENTS[format]({
              portfolioId: tenantLeasePortfolioId,
              customerId,
              accountId,
              start_date: startDate,
              end_date: endDate,
            }),
          )
          return
        }
        dispatch(
          STATEMENT_EVENTS[format]({
            customerId,
            accountId,
            start_date: startDate,
            end_date: endDate,
          }),
        )
      }
    },
    [customerId, accountId, dispatch, tenantLeasePortfolioId],
  )

  const fetchCustomerStatementPdf = useCallback(
    (startDate: string, endDate: string) => fetchCustomerStatement(startDate, endDate, 'pdf'),
    [fetchCustomerStatement],
  )
  const fetchCustomerStatementCsv = useCallback(
    (startDate: string, endDate: string) => fetchCustomerStatement(startDate, endDate, 'csv'),
    [fetchCustomerStatement],
  )
  const statementData = useSelector((state: RootState) =>
    accountingApiSelectors.getCustomerStatementJsonById(state)(customerId, accountId),
  )
  const statementPdf = useSelector((state: RootState) =>
    accountingApiSelectors.getCustomerStatementPdfById(state)(customerId, accountId),
  )
  const statementCsv = useSelector((state: RootState) =>
    accountingApiSelectors.getCustomerStatementCsvById(state)(customerId, accountId),
  )

  const isCompany = pathOr(false, ['context', 'customer', 'isCompany'], statementData)
  const customerName = isCompany
    ? pathOr('Account Statement', ['context', 'customer', 'companyName'], statementData)
    : pathOr('Account Statement', ['context', 'customer', 'name'], statementData)
  const fileName = isTenantLeaseWallet ? `Tenant Statement_${customerName}` : customerName

  const isOpen = customerId && accountId

  return useMemo(() => {
    return isOpen ? (
      <ViewStatementProvider
        onClose={() => redirect(`/contacts/${customerId}${contactFilterSearch}`)}
        onParamChange={fetchCustomerStatement}
        isOpen={isOpen}
        statementData={statementData}
        downloadStatement={fetchCustomerStatementPdf}
        statementPdf={statementPdf}
        pdfFileName={fileName}
        downloadStatementCsv={fetchCustomerStatementCsv}
        statementCsv={statementCsv}
        csvFileName={fileName}
      >
        <ViewStatement title="Account Statement" />
      </ViewStatementProvider>
    ) : null
  }, [
    redirect,
    contactFilterSearch,
    customerId,
    fetchCustomerStatement,
    fetchCustomerStatementPdf,
    fetchCustomerStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    isOpen,
    fileName,
  ])
}

export default subscribe([ContactsProvider], ({ match, redirect, contactFilterSearch }: $TSFixMe) => ({
  match,
  redirect,
  contactFilterSearch,
}))(CustomerStatement)
